import { css, keyframes, styled } from 'styled-components';
import { hex2rgba } from '../../../theme/interpolateColors.js';
const glowFadeIn = keyframes `
  from {
    opacity: 0;
  }
`;
const GLOW_ORBIT_STEPS = 24;
/*
 * The design moves orbs with rotate() translateX() rotate(): a circle in px, which leaves the orbs
 * in the middle of a wide button. The same circle traced with translate(x%, y%) scales x with the
 * orb width and y with its height, so the glow fills buttons of any size the same way.
 */
const glowOrbit = ({ startAngle, direction, radius, scale, widthToHeight }) => {
    const frames = Array.from({ length: GLOW_ORBIT_STEPS + 1 }, (_, step) => {
        const progress = step / GLOW_ORBIT_STEPS;
        const towardsMidCycle = 1 - Math.abs(1 - 2 * progress);
        const angle = ((startAngle + direction * 360 * progress) * Math.PI) / 180;
        const distance = radius[0] + (radius[1] - radius[0]) * towardsMidCycle;
        const x = distance * Math.cos(angle);
        const y = distance * widthToHeight * Math.sin(angle);
        const size = scale[0] + (scale[1] - scale[0]) * towardsMidCycle;
        return `${(progress * 100).toFixed(2)}% { transform: translate(${x.toFixed(2)}%, ${y.toFixed(2)}%) scale(${size.toFixed(3)}); }`;
    });
    return keyframes `${frames.join('\n')}`;
};
const glowOrbitSky = glowOrbit({ startAngle: 0, direction: 1, radius: [34, 46], scale: [1, 1.25], widthToHeight: 0.6 });
const glowOrbitIndigo = glowOrbit({ startAngle: 240, direction: -1, radius: [30, 44], scale: [1, 1.3], widthToHeight: 0.57 });
/* Clips the glow to the button and isolates it, so GlowLayer's negative z-index stays inside. */
export const glowingButton = css `
  overflow: hidden;
  isolation: isolate;
`;
/* Negative z-index inside the isolated button keeps the glow above its background and below the label. */
export const GlowLayer = styled.span `
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  animation: ${glowFadeIn} 420ms cubic-bezier(0.2, 0.7, 0.2, 1);
`;
export const GlowOrbs = styled.span `
  position: absolute;
  inset: 0;
  filter: blur(11px);
  opacity: ${({ theme }) => (theme.themeId === 'primaryDark' ? 0.95 : 0.65)};
`;
const GlowOrb = styled.span `
  position: absolute;
  border-radius: 999px;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
export const GlowOrbSky = styled(GlowOrb) `
  left: 15%;
  top: -122%;
  width: 70%;
  height: 200%;
  background: radial-gradient(closest-side, ${({ theme }) => theme.colors.main}, ${({ theme }) => hex2rgba(theme.colors.main, 0)});
  animation: ${glowOrbitSky} 3.2s linear infinite;
`;
export const GlowOrbIndigo = styled(GlowOrb) `
  left: 20%;
  top: -105%;
  width: 60%;
  height: 180%;
  background: radial-gradient(
    closest-side,
    ${({ theme }) => theme.colors.indigo500},
    ${({ theme }) => hex2rgba(theme.colors.indigo500, 0)}
  );
  animation: ${glowOrbitIndigo} 2.72s linear infinite;
`;
/* Fills the centre with the button background so the label keeps its contrast. */
export const GlowScrim = styled.span `
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse closest-side at 50% 50%,
    ${({ theme }) => hex2rgba(theme.colors.grey00_1, 0.95)} 12%,
    ${({ theme }) => hex2rgba(theme.colors.grey00_1, 0)} 85%
  );
`;
